import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, errorText, formatDate, formatGeld } from '../api';
import { KundeKasten, PositionenEditor, VorgangAktionen, VorgangKopf, VorgangVerlauf, positionAusApi, positionenFuerApi, type PositionEingabe, type Schritt } from '../components/Vorgang';
import { Card, Loading, Notice } from '../components/ui';
import type { Bestellung, Vorgang } from '../types';

const OFFENE_STATUS = [
  { wert: 'erfasst', label: 'Erfasst' },
  { wert: 'bestellt', label: 'Bestellt' },
  { wert: 'eingetroffen', label: 'Ware eingetroffen' },
  { wert: 'kunde_informiert', label: 'Kunde informiert' },
];

/** Die nächsten sinnvollen Schritte je Status - genau die Übergänge, die der Server erlaubt. */
function schritteFuer(b: Bestellung): Schritt[] {
  const abschluss: Schritt = { label: 'An Kunden übergeben – abschließen', abschluss: true };
  const teile: Schritt = { label: 'An Werkstatt übergeben – abschließen', abschluss: true };
  switch (b.status) {
    case 'erfasst':
      return [{ label: 'Beim Lieferanten bestellt', ziel: 'bestellt', primary: true, felder: ['lieferant'] }];
    case 'bestellt':
      return [{ label: 'Ware ist eingetroffen', ziel: 'eingetroffen', primary: true }];
    case 'eingetroffen':
      return b.serviceauftrag_id
        ? [teile]
        : [{ label: 'Kunde informiert', ziel: 'kunde_informiert', primary: true, notizLabel: 'Wie / was vereinbart' }, abschluss, { label: 'Zurück auf bestellt', ziel: 'bestellt', felder: ['lieferant'], notizPflicht: true, notizLabel: 'Warum (Falsch-/Teillieferung)' }];
    case 'kunde_informiert':
      return [abschluss, { label: 'Zurück auf bestellt', ziel: 'bestellt', felder: ['lieferant'], notizPflicht: true, notizLabel: 'Warum' }];
    default:
      return [];
  }
}

export function BestellungDetailPage() {
  const { id } = useParams();
  const [b, setB] = useState<Bestellung | null>(null);
  const [fehler, setFehler] = useState('');
  const [bearbeiten, setBearbeiten] = useState<PositionEingabe[] | null>(null);

  const laden = useCallback(() => {
    api<Bestellung>(`/api/bestellungen/${id}`)
      .then(setB)
      .catch((e) => setFehler(errorText(e)));
  }, [id]);
  useEffect(laden, [laden]);

  if (!b) return fehler ? <Notice kind="error">{fehler}</Notice> : <Loading />;
  const offen = b.status !== 'abgeschlossen' && b.status !== 'storniert';

  async function positionenSpeichern() {
    try {
      setB(await api<Bestellung>(`/api/bestellungen/${b!.id}`, { method: 'PATCH', body: { positionen: positionenFuerApi(bearbeiten!) } }));
      setBearbeiten(null);
    } catch (e) {
      setFehler(errorText(e));
    }
  }

  return (
    <>
      <VorgangKopf art="bestellung" v={b} titel={b.artikel ?? 'Bestellung'} />
      {fehler && <Notice kind="error">{fehler}</Notice>}
      <div className="spalten haupt-links">
        <div className="stack">
          <Card title="Was zu tun ist">
            <VorgangAktionen
              art="bestellung"
              v={b}
              schritte={schritteFuer(b)}
              onNeu={(v: Vorgang) => setB(v as Bestellung)}
              abschlussArten={b.serviceauftrag_id ? ['ueber_service', 'kasse', 'forderung', 'keine'] : ['kasse', 'forderung', 'keine']}
              offeneStatus={OFFENE_STATUS}
            />
            {(b.status === 'eingetroffen' || b.status === 'kunde_informiert') && !b.serviceauftrag_id && (
              <div style={{ marginTop: 10 }}>
                <Link className="button" to={`/lieferungen/neu?bestellung=${b.id}`}>
                  🚚 Lieferung dazu anlegen
                </Link>
              </div>
            )}
          </Card>
          <Card
            title="Positionen"
            actions={
              offen && !bearbeiten ? (
                <button className="klein" onClick={() => setBearbeiten((b.positionen ?? []).map(positionAusApi))}>
                  Bearbeiten
                </button>
              ) : null
            }
            padded={!!bearbeiten}
          >
            {bearbeiten ? (
              <div className="stack" style={{ gap: 10 }}>
                <PositionenEditor positionen={bearbeiten} onChange={setBearbeiten} />
                <div className="row" style={{ justifyContent: 'flex-end' }}>
                  <button onClick={() => setBearbeiten(null)}>Abbrechen</button>
                  <button className="primary" onClick={() => void positionenSpeichern()}>
                    Speichern
                  </button>
                </div>
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Artikel</th>
                    <th>Art.-Nr. / EAN</th>
                    <th style={{ textAlign: 'right' }}>Menge</th>
                    <th style={{ textAlign: 'right' }}>VK</th>
                  </tr>
                </thead>
                <tbody>
                  {(b.positionen ?? []).map((p) => (
                    <tr key={p.id}>
                      <td>{p.bezeichnung}</td>
                      <td className="small mono">{[p.artikelnummer, p.ean].filter(Boolean).join(' · ')}</td>
                      <td style={{ textAlign: 'right' }}>{p.menge}</td>
                      <td style={{ textAlign: 'right' }}>{formatGeld(p.preis_cent)}</td>
                    </tr>
                  ))}
                  {b.summe_cent != null && (
                    <tr>
                      <td colSpan={3} style={{ textAlign: 'right' }}>
                        <strong>Summe</strong>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <strong>{formatGeld(b.summe_cent)}</strong>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </Card>
          <VorgangVerlauf art="bestellung" v={b} onNeu={(v) => setB(v as Bestellung)} />
        </div>

        <Card title="Angaben">
          <div className="stack" style={{ gap: 12 }}>
            <KundeKasten v={b} />
            {b.serviceauftrag_id && (
              <div>
                <div className="small muted">Teile für</div>
                <Link to={`/service/${b.serviceauftrag_id}`}>Serviceauftrag {b.serviceauftrag_nummer}</Link>
              </div>
            )}
            <dl className="daten">
              <dt>Lieferant</dt>
              <dd>{b.lieferant ?? '—'}</dd>
              <dt>Bestellnr.</dt>
              <dd className="mono">{b.lieferant_bestellnr ?? '—'}</dd>
              <dt>Erwartet</dt>
              <dd>{formatDate(b.erwartet_am)}</dd>
              <dt>Standort</dt>
              <dd>{b.standort ?? '—'}</dd>
              <dt>Zuständig</dt>
              <dd>{b.zustaendig}</dd>
              <dt>Woher</dt>
              <dd>{b.quelle}</dd>
            </dl>
            {b.notiz && <div style={{ whiteSpace: 'pre-wrap' }}>{b.notiz}</div>}
          </div>
        </Card>
      </div>
    </>
  );
}
